import React, {useState, useCallback} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {Dialog, Transition, Tab} from '@headlessui/react';
import {XMarkIcon, EnvelopeIcon, PhoneIcon, CalendarDaysIcon} from '@heroicons/react/24/outline';
import {AdminService} from '@/services/adminService';
import {Ethnicities, States} from '@/services/constants';
import {UserSearchResult} from '@/types/api';
import {useAuth} from '@/hooks/useAuth';
import {useAppDispatch} from '@/hooks/redux';
import {setCurrentPatient} from '@/slices/patientSlice';
import copy from 'clipboard-copy';
import clsx from 'clsx';

interface Props {
    isModalHidden: boolean;
    setIsModalHidden: (value: boolean) => void;
    selectedUser: UserSearchResult;
    setSelectedUser: (value: UserSearchResult) => void;
    setPinnedMetrics?: (value: any) => void;
    onUserUpdated?: (user: UserSearchResult) => void;
}

/**
 * UserEditModal component - Modern modal for editing user information
 * Refactored to use Headless UI, Tailwind CSS, and modern patterns
 */
export const UserEditModal: React.FC<Props> = ({
    isModalHidden,
    setIsModalHidden,
    selectedUser,
    setSelectedUser,
    setPinnedMetrics: _setPinnedMetrics,
    onUserUpdated,
}) => {
    const queryClient = useQueryClient();
    const {user, setUser} = useAuth();
    const dispatch = useAppDispatch();

    const [activeTab, setActiveTab] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    // Mutation for updating user
    const updateUserMutation = useMutation({
        mutationFn: (userData: any) => AdminService.updateUser(userData),
        onSuccess: (updatedData, variables) => {
            queryClient.invalidateQueries({queryKey: ['admin', 'users']});
            // Update the selectedUser state with the saved data to keep the modal in sync
            setSelectedUser(variables);
            // Notify the parent component that the user was updated
            if (onUserUpdated) {
                onUserUpdated(variables);
            }
        },
        onError: () => {
            // Handle error silently or show user-friendly message
        },
    });

    // Mutation for password reset
    const resetPasswordMutation = useMutation({
        mutationFn: (email: string) => AdminService.resetUserPassword(email, ''),
        onSuccess: () => {
            // Show success message
        },
        onError: () => {
            // Handle error silently or show user-friendly message
        },
    });

    const handleUserInfoChange = useCallback(
        (field: string, value: any) => {
            setSelectedUser({
                ...selectedUser,
                [field]: value,
            });
        },
        [selectedUser, setSelectedUser]
    );

    const handlePhoneChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const digitReg = /^[0-9]$/;
            let value = event.target.value;

            if (value.length === 4 || value.length === 8) {
                if (value[value.length - 1] !== '-') {
                    value = value.substring(0, value.length - 1) + '-' + value[value.length - 1];
                } else {
                    value = value.substring(0, value.length - 1);
                }
            }

            if (value !== '' && (!digitReg.test(value[value.length - 1]) || value.length > 12)) {
                return;
            }

            handleUserInfoChange('phone', value);
        },
        [handleUserInfoChange]
    );

    const handleZipChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const digitReg = /^[0-9]$/;
            const value = event.target.value;

            if (value !== '' && (!digitReg.test(value[value.length - 1]) || value.length > 5)) {
                return;
            }

            handleUserInfoChange('zip', value);
        },
        [handleUserInfoChange]
    );

    const copyVerificationLink = useCallback(() => {
        if (selectedUser.token) {
            copy(`${window.location.origin}/verify/${selectedUser.token}`);
        }
    }, [selectedUser.token]);

    const handlePasswordReset = useCallback(() => {
        if (selectedUser.email && selectedUser.verified) {
            resetPasswordMutation.mutate(selectedUser.email);
        }
    }, [selectedUser.email, selectedUser.verified, resetPasswordMutation]);

    const handleImpersonateUser = useCallback(async () => {
        if (!selectedUser.email || selectedUser.email === 'null' || selectedUser.email === 'NULL') {
            alert('Please set an email before impersonating user');
            return;
        }

        setIsLoading(true);
        try {
            try {
                // Use the original impersonation API (matches old implementation)
                const impersonationResult = await AdminService.impersonateUser(selectedUser.email);

                // Store original admin data before impersonation (only if not already impersonating)
                if (!user?.userInfo?.impersonate_id) {
                    const originalAdminData = {
                        token: user?.token,
                        userInfo: user?.userInfo,
                        username: user?.username,
                    };
                    localStorage.setItem('originalAdminData', JSON.stringify(originalAdminData));
                } else {
                    // Handle case when user is not authenticated
                }

                // Update auth context with impersonated user data (matches old implementation)
                const newUserData = {
                    token: impersonationResult.result.token,
                    userInfo: impersonationResult.result.userInfo,
                    username: impersonationResult.result.userInfo.email,
                    isLoggedIn: true,
                };

                setUser(newUserData);

                // Update Redux current patient as well (matches old implementation)
                dispatch(
                    setCurrentPatient({
                        id: impersonationResult.result.userInfo.id,
                        firstName: impersonationResult.result.userInfo.firstName,
                        lastName: impersonationResult.result.userInfo.lastName,
                        fullName: impersonationResult.result.userInfo.fullName,
                        birthdate: impersonationResult.result.userInfo.birthday,
                    })
                );
            } catch {
                // Fallback: Use existing getUserById to get user data and keep current token
                const userDetails = await AdminService.getUserById(selectedUser.id);

                // Create impersonated user data using available information
                const simulatedUserInfo = {
                    id: selectedUser.id,
                    firstName: selectedUser.firstName,
                    lastName: selectedUser.lastName,
                    email: selectedUser.email,
                    phone: selectedUser.phone,
                    birthday: selectedUser.birthday,
                    // Add any additional fields that come from getUserById
                    ...userDetails,
                };

                // Get current auth state to preserve token
                const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

                // Update auth context with simulated impersonation (keeps current admin token)
                const newUserData = {
                    token: currentUser.token, // Keep admin token since we don't have impersonation endpoint
                    userInfo: simulatedUserInfo,
                    username: selectedUser.email,
                    isLoggedIn: true,
                };

                setUser(newUserData);

                // Update Redux current patient as well
                dispatch(
                    setCurrentPatient({
                        id: selectedUser.id,
                        firstName: selectedUser.firstName,
                        lastName: selectedUser.lastName,
                        fullName: `${selectedUser.firstName} ${selectedUser.lastName}`,
                        birthdate: selectedUser.birthday,
                    })
                );
            }

            // Close modal and navigate to dashboard
            setIsModalHidden(true);

            // Redirect to dashboard
            window.location.href = '/';
        } catch {
            alert('Failed to impersonate user. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, [selectedUser, setUser, dispatch, setIsModalHidden, user?.token, user?.userInfo, user?.username]);

    const handleSave = useCallback(() => {
        updateUserMutation.mutate(selectedUser);
    }, [selectedUser, updateUserMutation]);

    const handleSaveAndClose = useCallback(() => {
        updateUserMutation.mutate(selectedUser, {
            onSuccess: () => {
                setIsModalHidden(true);
            },
        });
    }, [selectedUser, updateUserMutation, setIsModalHidden]);

    if (isModalHidden) return null;

    return (
        <Transition appear show={!isModalHidden} as={React.Fragment}>
            <Dialog as="div" className="relative z-50" onClose={() => setIsModalHidden(true)}>
                <Transition.Child
                    as={React.Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-black bg-opacity-25" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={React.Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95">
                            <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all">
                                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                                    <Dialog.Title className="text-lg font-medium leading-6 text-gray-900">
                                        Edit User: {selectedUser.firstName} {selectedUser.lastName}
                                    </Dialog.Title>
                                    <button
                                        onClick={() => setIsModalHidden(true)}
                                        className="text-gray-400 hover:text-gray-500">
                                        <XMarkIcon className="h-6 w-6" />
                                    </button>
                                </div>

                                <Tab.Group selectedIndex={activeTab} onChange={setActiveTab}>
                                    <Tab.List className="flex space-x-1 bg-gray-100 p-1 mx-6 mt-6 rounded-lg">
                                        {['Patient Info', 'Address Info'].map((tab) => (
                                            <Tab
                                                key={tab}
                                                className={({selected}) =>
                                                    clsx(
                                                        'w-full rounded-lg py-2.5 text-sm font-medium leading-5',
                                                        'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2',
                                                        selected
                                                            ? 'bg-white text-blue-700 shadow'
                                                            : 'text-blue-700 hover:bg-white/[0.12] hover:text-blue-800'
                                                    )
                                                }>
                                                {tab}
                                            </Tab>
                                        ))}
                                    </Tab.List>

                                    <Tab.Panels className="p-6">
                                        <Tab.Panel className="space-y-6">
                                            {/* Patient Info Tab */}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        First Name
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={selectedUser.firstName || ''}
                                                        onChange={(e) =>
                                                            handleUserInfoChange('firstName', e.target.value)
                                                        }
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                        placeholder="John"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Last Name
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={selectedUser.lastName || ''}
                                                        onChange={(e) =>
                                                            handleUserInfoChange('lastName', e.target.value)
                                                        }
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                        placeholder="Doe"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Date of Birth
                                                    </label>
                                                    <div className="relative">
                                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                            <CalendarDaysIcon className="h-5 w-5 text-gray-400" />
                                                        </div>
                                                        <input
                                                            type="date"
                                                            value={
                                                                selectedUser.birthday
                                                                    ? new Date(selectedUser.birthday)
                                                                          .toISOString()
                                                                          .split('T')[0]
                                                                    : ''
                                                            }
                                                            onChange={(e) =>
                                                                handleUserInfoChange('birthday', e.target.value)
                                                            }
                                                            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                        />
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Ethnicity
                                                    </label>
                                                    <select
                                                        value={selectedUser.ethnicity || ''}
                                                        onChange={(e) =>
                                                            handleUserInfoChange('ethnicity', e.target.value)
                                                        }
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                                                        <option value="">Select ethnicity</option>
                                                        {Ethnicities.map((ethnicity) => (
                                                            <option key={ethnicity} value={ethnicity}>
                                                                {ethnicity}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Biological Sex
                                                    </label>
                                                    <div className="flex space-x-4">
                                                        <label className="flex items-center">
                                                            <input
                                                                type="radio"
                                                                name="gender"
                                                                value="M"
                                                                checked={selectedUser.gender === 'M'}
                                                                onChange={(e) =>
                                                                    handleUserInfoChange('gender', e.target.value)
                                                                }
                                                                className="mr-2"
                                                            />
                                                            Male
                                                        </label>
                                                        <label className="flex items-center">
                                                            <input
                                                                type="radio"
                                                                name="gender"
                                                                value="F"
                                                                checked={selectedUser.gender === 'F'}
                                                                onChange={(e) =>
                                                                    handleUserInfoChange('gender', e.target.value)
                                                                }
                                                                className="mr-2"
                                                            />
                                                            Female
                                                        </label>
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Email (Read Only)
                                                    </label>
                                                    <div className="relative">
                                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                            <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                                                        </div>
                                                        <input
                                                            type="email"
                                                            value={selectedUser.email || ''}
                                                            readOnly
                                                            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 cursor-not-allowed"
                                                        />
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Phone Number
                                                    </label>
                                                    <div className="relative">
                                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                            <PhoneIcon className="h-5 w-5 text-gray-400" />
                                                        </div>
                                                        <input
                                                            type="tel"
                                                            value={selectedUser.phone || ''}
                                                            onChange={handlePhoneChange}
                                                            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                            placeholder="555-555-5555"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </Tab.Panel>

                                        <Tab.Panel className="space-y-6">
                                            {/* Address Info Tab */}
                                            <div className="space-y-6">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Street Address
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={selectedUser.address1 || ''}
                                                        onChange={(e) =>
                                                            handleUserInfoChange('address1', e.target.value)
                                                        }
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                        placeholder="123 Apple Street"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Address Cont. - Apt/Suite/Etc (Optional)
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={selectedUser.address2 || ''}
                                                        onChange={(e) =>
                                                            handleUserInfoChange('address2', e.target.value)
                                                        }
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                        placeholder="Apt 1000"
                                                    />
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            City
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={selectedUser.city || ''}
                                                            onChange={(e) =>
                                                                handleUserInfoChange('city', e.target.value)
                                                            }
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                            placeholder="Dallas"
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            State
                                                        </label>
                                                        <select
                                                            value={selectedUser.state || ''}
                                                            onChange={(e) =>
                                                                handleUserInfoChange('state', e.target.value)
                                                            }
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                                                            <option value="">Select state</option>
                                                            {States.map((state) => (
                                                                <option key={state.code} value={state.code}>
                                                                    {state.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Zip Code
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={selectedUser.zip || ''}
                                                            onChange={handleZipChange}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                            placeholder="75070"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </Tab.Panel>
                                    </Tab.Panels>
                                </Tab.Group>

                                {/* Action Buttons */}
                                <div className="px-6 py-4 border-t border-gray-200 space-y-4">
                                    {/* User Status and Actions */}
                                    <div className="flex flex-wrap items-center gap-3">
                                        <button
                                            onClick={() => handleUserInfoChange('verified', !selectedUser.verified)}
                                            className={clsx(
                                                'px-3 py-2 rounded-md text-sm font-medium',
                                                selectedUser.verified
                                                    ? 'bg-green-100 text-green-800 border border-green-200'
                                                    : 'bg-red-100 text-red-800 border border-red-200'
                                            )}>
                                            {selectedUser.verified ? 'Verified' : 'Not Verified'}
                                        </button>

                                        <button
                                            onClick={handlePasswordReset}
                                            disabled={!selectedUser.verified || resetPasswordMutation.isPending}
                                            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                                            Reset Password
                                        </button>

                                        <button
                                            onClick={copyVerificationLink}
                                            disabled={selectedUser.verified}
                                            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                                            Copy Verification Link
                                        </button>

                                        <button
                                            onClick={handleImpersonateUser}
                                            disabled={isLoading}
                                            className="px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
                                            {isLoading ? 'Processing...' : 'Impersonate User'}
                                        </button>
                                    </div>

                                    {/* Save Buttons */}
                                    <div className="flex justify-end space-x-3">
                                        <button
                                            onClick={handleSave}
                                            disabled={updateUserMutation.isPending}
                                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
                                            {updateUserMutation.isPending ? 'Saving...' : 'Save'}
                                        </button>
                                        <button
                                            onClick={handleSaveAndClose}
                                            disabled={updateUserMutation.isPending}
                                            className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-100 border border-blue-300 rounded-md hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed">
                                            {updateUserMutation.isPending ? 'Saving...' : 'Save and Close'}
                                        </button>
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default UserEditModal;
